/* eslint-disable max-lines-per-function */
import { useRef } from 'react';
import { useVisualEditorContext } from './VisualEditorContext';
import { MARGIN } from './constants';
import { GridRows } from '@visx/grid';
import {
  AxisBottom,
  AxisLeft,
} from '@/domain/dienstplan/features/VisualEditor/components/Axis';
import { useParams } from 'react-router-dom';
import { Line } from '@visx/shape';
import { BelegungBar } from '@/domain/dienstplan/features/VisualEditor/components/Belegung/Belegung';
import { useTranslation } from 'react-i18next';
import { localPoint } from '@visx/event';
import { useVisualEditor } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/useVisualEditor';
import { Cursor } from '@/domain/dienstplan/features/VisualEditor/components/Cursor';
import { BelegungPopover } from '@/domain/dienstplan/features/VisualEditor/components/BelegungPopover/BelegungPopover';

export function VisualEditor() {
  const { weekday } = useParams();
  const { t } = useTranslation();

  const {
    yScaleAxis,
    xMax,
    yMax,
    width,
    height,
    selectedArbeitsbereich,
    maxMitarbeiterAnzahl,
  } = useVisualEditorContext();

  const { emitter, belegungen, store } = useVisualEditor();

  const svgRef = useRef<SVGSVGElement>(null);

  const {
    ui: { popoverPosition, hoveredBelegung, isPopoverOpen, xAxisOffset },
    setIsPopoverOpen,
    setPopoverPosition,
  } = store;

  return (
    <div className="relative select-none" style={{ width, height }}>
      {popoverPosition && hoveredBelegung && (
        <BelegungPopover
          key={hoveredBelegung.id}
          open={isPopoverOpen}
          setOpen={setIsPopoverOpen}
          store={store}
          triggerStyle={popoverPosition}
          popoverRect={hoveredBelegung}
          rects={belegungen}
          setPopoverPosition={setPopoverPosition}
        />
      )}
      <svg
        id="visual-editor"
        shapeRendering="crispEdges"
        ref={svgRef}
        width={width}
        height={height}
        role="img"
        onMouseDown={(event) => {
          if (!svgRef.current) return;
          const point = localPoint(svgRef.current, event);
          emitter.emit('visualEditorMouseDown', { point });
        }}
        onMouseMove={(event) => {
          if (!svgRef.current) return;
          const point = localPoint(svgRef.current, event);
          emitter.emit('visualEditorMouseMove', { point });
        }}
        onMouseUp={() => emitter.emit('visualEditorMouseUp')}
        onMouseLeave={() => emitter.emit('visualEditorMouseLeave')}
        aria-label={t(
          'domain.dienstplan.mindestbelegung.visualEditor.labels.ariaLabel',
        )}>
        <GridRows
          left={MARGIN.left}
          top={MARGIN.top}
          width={xMax}
          height={yMax}
          scale={yScaleAxis}
        />
        <AxisLeft />
        <AxisBottom offset={xAxisOffset} />
        {selectedArbeitsbereich !== null && (
          <>
            {belegungen
              .filter(
                (rect) =>
                  !selectedArbeitsbereich ||
                  rect.belegung.arbeitsbereich.id === selectedArbeitsbereich.id,
              )
              .filter((rect) => rect.belegung.tag === weekday)
              .map((intervall) => {
                return (
                  <BelegungBar
                    offset={xAxisOffset}
                    key={intervall.belegung.id}
                    belegung={intervall}
                    onMouseLeave={(event) =>
                      emitter.emit('belegungMouseLeave', { event })
                    }
                    onMouseMove={(event) => {
                      if (!svgRef.current) return;
                      const point = localPoint(svgRef.current, event);

                      emitter.emit('belegungMouseMove', {
                        belegung: intervall,
                        point,
                        event,
                      });
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      if (!svgRef.current) return;
                      const point = localPoint(svgRef.current, event);

                      emitter.emit('belegungMouseDown', {
                        belegung: intervall,
                        point,
                      });
                    }}
                    onMouseUp={() => emitter.emit('belegungMouseUp')}
                  />
                );
              })}
            <Line
              from={{
                x: MARGIN.left,
                y: MARGIN.top + yScaleAxis(maxMitarbeiterAnzahl.value),
              }}
              to={{
                x: MARGIN.left + xMax,
                y: MARGIN.top + yScaleAxis(maxMitarbeiterAnzahl.value),
              }}
              stroke="#FF3B30"
              strokeDasharray="6,3"
              strokeWidth={2}
              style={{ pointerEvents: 'none' }}
            />
          </>
        )}
        <Cursor
          circle={{
            cx: store.ui.cursorPosition?.x ?? 0,
            cy: store.ui.cursorPosition?.y ?? 0,
            r: 3,
          }}
        />
      </svg>
    </div>
  );
}
